import React, { useState, useEffect } from 'react';
import GameIcon from './GameIcon';

export default function StoryIntro({ mission, items = [], coins, gameSettings = {}, onStart, onBack }) {
  const [displayText, setDisplayText] = useState('');
  const [showButton, setShowButton] = useState(false);

  const storyText = mission.story_text || '';

  const budget = mission.budget_limit || gameSettings.default_budget || 0;
  const timeLimit = mission.time_limit_seconds || gameSettings.default_time_limit || 480;
  const minutes = Math.floor(timeLimit / 60);

  // Typing effect
  useEffect(() => {
    if (!storyText) {
      setShowButton(true);
      return;
    }
    let idx = 0;
    setDisplayText('');
    setShowButton(false);
    const interval = setInterval(() => {
      idx++;
      setDisplayText(storyText.slice(0, idx));
      if (idx >= storyText.length) {
        clearInterval(interval);
        setTimeout(() => setShowButton(true), 300);
      }
    }, 25);
    return () => clearInterval(interval);
  }, [storyText]);

  const occasionIcons = {
    wedding: '💍', beach: '🏖️', winter: '❄️', school: '📚',
    work: '💼', casual: '👕', traditional: '🕌', party: '🎉',
    eid: '🌙', formal: '🎩', sport: '⚽', date: '💐',
  };
  const occasionKey = typeof mission.occasion === 'string' ? mission.occasion.toLowerCase() : '';
  const icon = mission.icon || gameSettings[`icon_occasion_${occasionKey}`] || occasionIcons[occasionKey] || '🎯';

  try {
    return (
      <div className="min-h-screen bg-[#F5F0E8] flex items-center justify-center p-4 animate-fade-up">
        <div className="max-w-lg w-full">
          {/* Story Card */}
          <div className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden">
            {/* Top pattern accent */}
            <div className="h-2 bg-gradient-to-r from-[#C84B31] via-[#D4A843] to-[#2D6A4F]" />

            <div className="p-6 sm:p-8">
              {/* Mission icon */}
              <div className="text-center mb-6">
                <span className="text-6xl block mb-3"><GameIcon icon={icon} /></span>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">
                  {mission.name_english || mission.name || 'New Mission'}
                </h2>
                {mission.name_darija && (
                  <p className="text-[#2D6A4F] font-medium mt-1">{mission.name_darija}</p>
                )}
              </div>

              {/* Story text with typing effect */}
              {storyText && (
                <div className="bg-[#F5F0E8] rounded-xl p-4 mb-6 min-h-[80px]">
                  <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                    {displayText}
                    {displayText.length < storyText.length && (
                      <span className="typing-cursor" />
                    )}
                  </p>
                </div>
              )}

              {/* Mission constraints */}
              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
                  <span className="text-lg block mb-1"><GameIcon icon={gameSettings.icon_token || '🪙'} /></span>
                  <span className="text-base font-bold text-amber-800">{budget > 0 ? budget : '∞'}</span>
                  <span className="text-[10px] text-amber-600 block">{budget > 0 ? (gameSettings.story_budget_label || 'Budget') : (gameSettings.story_no_limit || 'No limit')}</span>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-center">
                  <span className="text-lg block mb-1">⏱</span>
                  <span className="text-base font-bold text-blue-800">{minutes}min</span>
                  <span className="text-[10px] text-blue-600 block">{gameSettings.story_time_label || 'Time'}</span>
                </div>
                <div className="bg-teal-50 border border-teal-200 rounded-xl p-3 text-center">
                  <span className="text-lg block mb-1"><GameIcon icon={gameSettings.icon_default_item || '👕'} /></span>
                  <span className="text-base font-bold text-teal-800">
                    {Array.isArray(mission.required_items) ? mission.required_items.length : '?'}
                  </span>
                  <span className="text-[10px] text-teal-600 block">{gameSettings.story_items_label || 'Items'}</span>
                </div>
              </div>

              {/* Star criteria hint */}
              <div className="bg-gray-50 rounded-xl p-3 mb-6 border border-gray-100">
                <p className="text-xs font-semibold text-gray-600 mb-2">{gameSettings.story_earn_stars || 'Earn 3 Stars:'}</p>
                <div className="space-y-1">
                  {(mission.star_criteria?.all_items !== false) && (
                    <p className="text-xs text-gray-500 flex items-center gap-2">
                      <span className="text-amber-400">★</span> {gameSettings.story_star_items || 'Find all required items'}
                    </p>
                  )}
                  {(mission.star_criteria?.under_budget !== false) && (
                    <p className="text-xs text-gray-500 flex items-center gap-2">
                      <span className="text-amber-400">★</span> {gameSettings.story_star_budget || 'Stay under budget'}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 flex items-center gap-2">
                    <span className="text-amber-400">★</span> {(gameSettings.story_star_darija || 'Use {N} Darija phrases while bargaining').replace('{N}', mission.star_criteria?.darija_phrases_min || 3)}
                  </p>
                </div>
              </div>

              {/* Required items preview */}
              {Array.isArray(mission.required_items) && mission.required_items.length > 0 && (
                <div className="bg-teal-50 rounded-xl p-3 mb-6 border border-teal-200">
                  <p className="text-xs font-semibold text-teal-700 mb-2">{gameSettings.story_items_needed || 'Items to Find:'}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {mission.required_items.map((ri, idx) => {
                      // Resolve item name: ri can be a string ID or object with item_id
                      const itemId = typeof ri === 'string' ? ri : (ri?.item_id || ri?.id);
                      const fullItem = items.find(i => i.id === itemId);
                      const name = fullItem?.name_english || fullItem?.category || ri?.name_english || ri?.category || ri?.name || itemId || 'Item';
                      return (
                        <span key={idx} className="text-xs bg-white border border-teal-200 rounded-lg px-2.5 py-1 text-teal-800 font-medium">
                          {name}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-3">
                <button
                  onClick={onBack}
                  className="px-5 py-3 rounded-xl border-2 border-gray-200 text-gray-600 font-semibold text-sm hover:border-gray-300 transition-colors"
                >
                  {gameSettings.story_back_button || 'Back'}
                </button>
                <button
                  onClick={onStart}
                  disabled={!showButton}
                  className={`flex-1 py-3 rounded-xl font-bold text-sm transition-all ${
                    showButton
                      ? 'bg-[#2D6A4F] hover:bg-[#40916C] text-white shadow-md'
                      : 'bg-gray-200 text-gray-400 cursor-wait'
                  }`}
                >
                  {showButton ? (gameSettings.story_enter_button || 'Enter the Souk →') : 'Loading...'}
                </button>
              </div>
            </div>
          </div>

          {/* Educational note */}
          <p className="text-center text-[10px] text-gray-400 mt-4">
            {gameSettings.story_disclaimer || 'Educational game — learn Darija bargaining phrases'}
          </p>
        </div>
      </div>
    );
  } catch (err) {
    console.error('[StoryIntro] Render error:', err);
    return (
      <div className="min-h-screen bg-[#F5F0E8] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-sm text-center">
          <span className="text-5xl block mb-3">🎯</span>
          <h2 className="text-xl font-bold mb-4">{mission?.name_english || mission?.name || 'Mission'}</h2>
          <p className="text-sm text-red-500 mb-4">Display error: {err?.message}</p>
          <div className="flex gap-3">
            <button onClick={onBack} className="px-4 py-2 rounded-xl border-2 border-gray-200 text-gray-600 font-medium">Back</button>
            <button onClick={onStart} className="flex-1 py-2 rounded-xl bg-[#2D6A4F] text-white font-bold">Start Mission</button>
          </div>
        </div>
      </div>
    );
  }
}
